import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { IRevaultInformation } from 'features/payment/types';
import { mapCardTypeToLoyalty, shouldReVault } from 'features/payment/utils';
import { isApplePay, isCashApp, isGooglePay, isPayPal, isVenmo } from 'features/payment/utils';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { getStoreLocalTime } from 'features/store/utils';
import { useIntl } from 'react-intl';
import { GestureResponderEvent } from 'react-native';

import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { ApplePayButton } from 'components/payments/integrations/firstdata/applepay-button';
import { CashAppButton } from 'components/payments/integrations/firstdata/cashapp/cashapp-button';
import { GooglePayButton } from 'components/payments/integrations/firstdata/googlepay-button';
import PayPalButton from 'components/payments/integrations/firstdata/paypal-button';
import { IBraintreeResult, ICashAppResult } from 'components/payments/integrations/firstdata/types';
import { removeAllQueryParams } from 'components/payments/integrations/firstdata/utils';
import VenmoButton from 'components/payments/integrations/firstdata/venmo-button';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { CartPaymentType } from 'generated/graphql-gateway';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import { useCart } from 'hooks/use-cart';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useOrderIsValidCheck } from 'pages/cart/hooks/use-order-is-valid-check';
import { PlaceOrderButton } from 'pages/cart/place-order-button';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode } from 'utils/service-mode';

import { logCartContinueOrder } from '../utils';

import { PaymentFooterContainer } from './payment-redesign/payment-footer-container';
import { useOrderPayment } from './use-order-payment';

export const PaymentFooter: React.FC = () => {
  const { cartEntries, calculateCartTotal, calculateCartTotalWithDiscount } = useCart();
  const isOrderBayEnabled = useFlag(LaunchDarklyFlag.ENABLE_ORDER_BAY);
  const {
    serverOrder,
    shouldSkipPriceOrder,
    priceOrderSuccess,
    unavailableCartEntries,
    priceMutationError,
    tipAmount,
    setIsTipLocked,
  } = useCartContext();

  const newPaymentValueIdRef = useRef('');

  const { navigate } = useNavigation();
  const router = useRouter();

  const { formatMessage } = useIntl();

  const { store } = useStoreContext();
  const { isServiceModeAvailable } = useStoreAvailabilityFns();

  const {
    addPaymentMethod,
    checkoutPaymentMethodId,
    isFirstData,
    isOrbital,
    setCheckoutPaymentMethodId,
    setEncryptionResult,
    storePaymentDetails,
    clearPaymentDetails,
    transformPaymentValues,
    orderGrandTotal: grandTotal,
    noPaymentMethodsAvailable,
    setIsCheckoutErrorsModalOpen,
    setProcessingPayment,
    showCashAppErrorMessage,
  } = usePaymentContext();

  const [hasAddPaymentMethodError, setAddPaymentMethodError] = useState(false);

  const { loyaltyUser } = useLoyaltyUser();
  const { evaluateLoyaltyUserIncentives } = useLoyaltyContext();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const {
    cardIsValid,
    commitOrder,
    confirmStoreOnCheckout,
    handlePaymentMethodSelected,
    isProcessingPayment,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    openServiceModeUnavailableErrorDialog,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadPaymentValues,
    showAddPaymentMethod: isAddNewCardMethodSelected,
    showReloadPaymentMethods,
    validateCreditCardForm,
    isAddGiftCardMethodSelected,
  } = useOrderPayment();

  const { isDelivery, serviceMode } = useServiceModeContext();

  const { selectedReloadPaymentMethod } = usePrepaidsPaymentMethods();

  const [isEncryptionActive, setIsEncryptionActive] = useState(false);

  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const enableOnetimePaymentAfterVault = useFlag(
    LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
  );

  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();
  const checkoutPriceMinimum = isDelivery ? checkoutDeliveryPriceMinimum : 0;

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? calculateCartTotal()
      : Number(serverOrder?.cart?.subTotalCents);
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;

  const { getErrorMessages, getLoyaltyPaymentErrorMessages } = useGetPaymentErrors({
    cartPriceTooLow,
  });

  const pricedAndAvailable =
    priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;
  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: false,
    invalidPaymentMethodError:
      !cardIsValid || !isValidReloadPaymentMethodWithCard || hasAddPaymentMethodError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const isLoading = isProcessingPayment || evaluatingIncentives;

  const isRbiOrderIdValid = useOrderIsValidCheck();
  useEffect(() => {
    if (!isRbiOrderIdValid && !isLoading) {
      navigate(routes.cart, { replace: true });
    }
  }, [isLoading, isRbiOrderIdValid, navigate]);

  const handleAddPaymentMethod = useCallback(
    async (encryptionResult?: IEncryptionResult) => {
      try {
        if (isOrbital && !encryptionResult) {
          throw new Error('Orbital encryption results not available');
        }
        const newPaymentValueId = await addPaymentMethod(
          transformPaymentValues({
            paymentValues,
            paymentFieldVariations,
          }),
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );
        setCheckoutPaymentMethodId(newPaymentValueId);
        handlePaymentMethodSelected(newPaymentValueId);
        return newPaymentValueId;
      } catch (error) {
        setAddPaymentMethodError(true);
        setIsCheckoutErrorsModalOpen(true);
        return null;
      }
    },
    [
      addPaymentMethod,
      isOrbital,
      handlePaymentMethodSelected,
      paymentFieldVariations,
      paymentValues,
      setCheckoutPaymentMethodId,
      setIsCheckoutErrorsModalOpen,
      transformPaymentValues,
    ]
  );

  const loyaltyPaymentMethodErrors = getLoyaltyPaymentErrorMessages();
  const errorMessages = useMemo(() => {
    return [...paymentErrorMessages, ...loyaltyPaymentMethodErrors];
  }, [paymentErrorMessages, loyaltyPaymentMethodErrors]);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  const handleOnSubmit = useCallback(
    async (event?: GestureResponderEvent, encryptionResult?: IEncryptionResult) => {
      if (confirmStoreOnCheckout && !isDelivery) {
        logCartContinueOrder();
      }
      if (event) {
        event.preventDefault();
      }

      if (isAddNewCardMethodSelected && !validateCreditCardForm(true)) {
        return;
      }

      if (
        isAddNewCardMethodSelected &&
        validateCreditCardForm() &&
        !checkoutPaymentMethodId &&
        paymentValues.saveCard
      ) {
        const skipVaultStep = enableOnetimePaymentAfterVault && isFirstData;
        if (!skipVaultStep) {
          const paymentId = await handleAddPaymentMethod(encryptionResult);

          if (!paymentId) {
            return;
          }

          newPaymentValueIdRef.current = paymentId;
        }
      }
      const loyaltyId = loyaltyUser?.id;
      if (loyaltyId && isAddNewCardMethodSelected && appliedLoyaltyOffers.length) {
        const paymentMethod = mapCardTypeToLoyalty(paymentValues.cardType);

        await evaluateLoyaltyUserIncentives(
          loyaltyId,
          appliedLoyaltyOffers,
          cartEntries,
          appliedRewards,
          positiveCartTotal,
          paymentMethod
        );
      }

      const serviceModeIsNotAvailableOrIsDisabled = !isServiceModeAvailable(
        store,
        serviceMode,
        getStoreLocalTime()
      );

      if (serviceModeIsNotAvailableOrIsDisabled) {
        openServiceModeUnavailableErrorDialog({
          message: formatMessage(
            { id: 'currentServiceModeUnavailable' },
            {
              // TODO: BKPE-6812 ORDER_BAY_PILOT. Remove or consolidate this logic once the pilot is done
              serviceMode: getLocalizedServiceMode(serviceMode, formatMessage, isOrderBayEnabled),
            }
          ),
        });
        return;
      }

      if (errorMessages.length > 0) {
        setIsCheckoutErrorsModalOpen(true);
      } else if (!isDelivery && confirmStoreOnCheckout) {
        if (!enableOnetimePaymentAfterVault || !isFirstData) {
          // If it is a one time payment with a newly added card, we want to store the info to place the order (in case of Curbside or page refresh).
          // Otherwise we clear the localStorage, because `paymentValues.saveCard` can be false due to stale data (see BKPE-256).
          if (!paymentValues.saveCard) {
            if (isAddNewCardMethodSelected) {
              storePaymentDetails({ state: paymentValues });
            } else {
              clearPaymentDetails();
            }
          }
        } else {
          // skipping standalone vault step for first data
          if (isAddNewCardMethodSelected) {
            storePaymentDetails({ state: paymentValues });
          } else {
            clearPaymentDetails();
          }
        }

        router.navigate(routes.cartStoreConfirmation);
      } else {
        commitOrder();
        setProcessingPayment(true);
      }
    },
    [
      confirmStoreOnCheckout,
      isDelivery,
      isAddNewCardMethodSelected,
      validateCreditCardForm,
      checkoutPaymentMethodId,
      paymentValues,
      loyaltyUser?.id,
      appliedLoyaltyOffers,
      isServiceModeAvailable,
      store,
      serviceMode,
      errorMessages.length,
      enableOnetimePaymentAfterVault,
      isFirstData,
      handleAddPaymentMethod,
      evaluateLoyaltyUserIncentives,
      cartEntries,
      appliedRewards,
      positiveCartTotal,
      openServiceModeUnavailableErrorDialog,
      formatMessage,
      isOrderBayEnabled,
      setIsCheckoutErrorsModalOpen,
      router,
      storePaymentDetails,
      clearPaymentDetails,
      commitOrder,
      setProcessingPayment,
    ]
  );

  const handlePayPal = useCallback(
    ({ nonce, deviceData, details }: IBraintreeResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.PAYPAL);
      handlePaymentMethodSelected(CartPaymentType.PAYPAL);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.PAYPAL,
        paypalDetails: {
          nonce,
          deviceData,
          details,
        },
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handlePayPalError = useCallback(
    (error: Error) => {
      logger.error({ error, message: 'PayPal error' });
      if (checkoutPaymentMethodId === CartPaymentType.PAYPAL) {
        setIsCheckoutErrorsModalOpen(true);
      }
    },
    [checkoutPaymentMethodId, setIsCheckoutErrorsModalOpen]
  );

  const handleVenmo = useCallback(
    ({ nonce, deviceData, details }: IBraintreeResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.VENMO);
      handlePaymentMethodSelected(CartPaymentType.VENMO);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.VENMO,
        venmoDetails: {
          details,
          deviceData,
          nonce,
        },
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handleVenmoError = useCallback(
    (error: Error) => {
      logger.error({ error, message: 'Venmo error' });
      setIsCheckoutErrorsModalOpen(true);
    },
    [setIsCheckoutErrorsModalOpen]
  );

  const handleCashApp = useCallback(
    (result: ICashAppResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.CASHAPP);
      handlePaymentMethodSelected(CartPaymentType.CASHAPP);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.CASHAPP,
        cashappDetails: result,
      });
      removeAllQueryParams();
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handleCashAppError = useCallback(
    (error: any) => {
      logger.error({ orderId: serverOrder?.rbiOrderId, error, message: 'Cash App error' });
      setIsCheckoutErrorsModalOpen(true);
    },
    [serverOrder?.rbiOrderId, setIsCheckoutErrorsModalOpen]
  );

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected,
    selectedMethodExist: !!selectedMethod,
  });
  const isAddNewPaymentMethodTypeSelected =
    currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD;

  const isPlaceOrderButtonDisabled =
    isProcessingPayment ||
    (!!selectedMethod?.prepaid && !enableGiftCard) ||
    isAddNewPaymentMethodTypeSelected ||
    (showReloadPaymentMethods && !!paymentMethods.length && !reloadPaymentValues.saveCard);

  const handleOnPress = useCallback(() => {
    if (isOrbital && !selectedMethod?.accountIdentifier) {
      return setIsEncryptionActive(true);
    }
    const accountIdentifier =
      selectedMethod?.accountIdentifier ?? selectedMethod?.fdAccountId ?? '';
    const revaultInformation: IRevaultInformation = shouldReVault({
      accountIdentifier,
      isOrbitalPaymentEnabled: isOrbital,
      methods: paymentMethods,
    });

    const isRevaultRequired = revaultInformation.isExpired || revaultInformation.isInvalid;

    if (isRevaultRequired) {
      router.navigate(
        `${routes.addCard}?accountToDelete=${selectedMethod?.accountIdentifier}&isInCart=true`
      );
    }
    handleOnSubmit();
  }, [
    handleOnSubmit,
    isOrbital,
    paymentMethods,
    router,
    selectedMethod?.accountIdentifier,
    selectedMethod?.fdAccountId,
  ]);

  const onEncryptionResult = useCallback(
    (data: IEncryptionResult) => {
      setIsEncryptionActive(false);
      setEncryptionResult(data);
      handleOnSubmit(undefined, data);
    },
    [handleOnSubmit, setEncryptionResult]
  );

  // Handle encryption error
  const onEncryptionError = (error: IEncryptionError) => {
    setIsEncryptionActive(false);
    setIsCheckoutErrorsModalOpen(true);
    const errorMessage = error.error;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
  };

  const isSelectedMethodDigitalWallet =
    selectedMethod &&
    (isPayPal(selectedMethod.accountIdentifier) ||
      isVenmo(selectedMethod.accountIdentifier) ||
      isGooglePay(selectedMethod.accountIdentifier) ||
      isApplePay(selectedMethod.accountIdentifier) ||
      isCashApp(selectedMethod.accountIdentifier));

  const isSelectedReloadPaymentMethodValid =
    showReloadPaymentMethods &&
    (isApplePay(selectedReloadPaymentMethod?.accountIdentifier) ||
      isGooglePay(selectedReloadPaymentMethod?.accountIdentifier));

  const shouldRenderPlaceOrderButton =
    !isSelectedMethodDigitalWallet && !isSelectedReloadPaymentMethodValid;

  return (
    <>
      {shouldRenderPlaceOrderButton && (
        <PaymentFooterContainer
          CTAButton={
            <PlaceOrderButton
              confirmation={!confirmStoreOnCheckout || isDelivery}
              isLoading={isLoading}
              disabled={
                isPlaceOrderButtonDisabled ||
                isProcessingPayment ||
                (showReloadPaymentMethods && !selectedReloadPaymentMethod?.accountIdentifier) ||
                noPaymentMethodsAvailable
              }
              placeOrderAriaLabel={placeOrderAriaLabel}
              onPress={handleOnPress}
            />
          }
          totalValue={grandTotal}
          isLoading={isLoading}
          showAddPaymentLabel={noPaymentMethodsAvailable}
        />
      )}

      {!!selectedMethod && isPayPal(selectedMethod.accountIdentifier) && (
        <PaymentFooterContainer
          CTAButton={
            <PayPalButton
              serverOrder={serverOrder}
              onResult={handlePayPal}
              onError={handlePayPalError}
              showLoading={isLoading}
            />
          }
          totalValue={grandTotal}
          isLoading={isLoading}
        />
      )}

      {!!selectedMethod && isVenmo(selectedMethod.accountIdentifier) && (
        <PaymentFooterContainer
          CTAButton={
            <VenmoButton
              serverOrder={serverOrder}
              onResult={handleVenmo}
              onError={handleVenmoError}
              showLoading={isLoading}
            />
          }
          totalValue={grandTotal}
          isLoading={isLoading}
        />
      )}

      {!!selectedMethod && isCashApp(selectedMethod.accountIdentifier) && (
        <PaymentFooterContainer
          CTAButton={
            !showCashAppErrorMessage ? (
              <CashAppButton
                serverOrder={serverOrder}
                onResult={handleCashApp}
                onError={handleCashAppError}
                showLoading={isLoading}
                tipAmount={tipAmount}
                setIsTipLocked={setIsTipLocked}
              />
            ) : (
              <PlaceOrderButton disabled confirmation={false} />
            )
          }
          hadMinimumCashAppError={showCashAppErrorMessage}
          totalValue={grandTotal}
          isLoading={isLoading}
        />
      )}

      {!!selectedMethod && isGooglePay(selectedMethod.accountIdentifier) && (
        <PaymentFooterContainer
          CTAButton={<GooglePayButton onPress={handleOnPress} isLoading={isLoading} />}
          totalValue={grandTotal}
          isLoading={isLoading}
        />
      )}

      {showReloadPaymentMethods &&
        !!selectedReloadPaymentMethod &&
        isGooglePay(selectedReloadPaymentMethod?.accountIdentifier) && (
          <PaymentFooterContainer
            CTAButton={<GooglePayButton onPress={handleOnPress} isLoading={isLoading} />}
            totalValue={grandTotal}
            isLoading={isLoading}
          />
        )}

      {!!selectedMethod && isApplePay(selectedMethod.accountIdentifier) && (
        <PaymentFooterContainer
          CTAButton={<ApplePayButton onPress={handleOnPress} isLoading={isLoading} />}
          totalValue={grandTotal}
          isLoading={isLoading}
        />
      )}

      {showReloadPaymentMethods &&
        !!selectedReloadPaymentMethod &&
        isApplePay(selectedReloadPaymentMethod?.accountIdentifier) && (
          <PaymentFooterContainer
            CTAButton={<ApplePayButton onPress={handleOnPress} isLoading={isLoading} />}
            totalValue={grandTotal}
            isLoading={isLoading}
          />
        )}

      {isOrbital && isEncryptionActive && (
        <SafetechEncryption
          cardNumber={paymentValues.cardNumber}
          cvv={paymentValues.cvv || ''}
          onResult={onEncryptionResult}
          onError={onEncryptionError}
        />
      )}
    </>
  );
};
